import React from 'react';
import { InputNumber, message, Select, Switch } from 'antd';
import type { AdminProductStockControlProps } from '../../interfaces';
import AdminProductMaterialImages from './AdminProductMaterialImages';
import AdminProductVariantStockList from './AdminProductVariantStockList';
import './AdminProductStockControl.scss';

const AdminProductStockControl: React.FC<AdminProductStockControlProps> = ({
  material,
  draftStockById,
  draftPrice,
  isSavingStock,
  isSavingPrice,
  isSavingSizes,
  requiresSizes,
  sizes,
  productId,
  onStockStep,
  onDraftStockInput,
  onDraftPriceInput,
  onCommitDraftStock,
  onCommitDraftPrice,
  onCommitMaterialSizes,
  onAvailabilityChange,
  onVariantAvailabilityChange,
  onUploadMaterialImages,
  onDeleteMaterialImage,
}) => {
  const allowedSizeIds = sizes.map((size) => size.id);

  return (
    <div className="admin-product-form__stock-row">
      <div className="admin-product-form__stock-control">
        <label className="admin-product-form__stock-field">
          <span>Material Available</span>
          <Switch
            checked={material.isAvailable}
            onChange={(checked) => void onAvailabilityChange(material.id, checked)}
          />
        </label>
        <label className="admin-product-form__stock-field">
          <span>Price</span>
          <InputNumber
            min={1}
            step={1}
            value={draftPrice}
            onChange={(value) => onDraftPriceInput(material.id, value)}
            onBlur={() => onCommitDraftPrice(material.id, material.price)}
            disabled={isSavingPrice}
            controls={false}
            className="admin-product-form__price-input"
          />
        </label>
        <label className="admin-product-form__stock-field">
          <span>Sizes</span>
          <Select
            mode="multiple"
            allowClear
            disabled={isSavingSizes}
            value={material.sizes?.map((size) => size.id).filter((id): id is string => Boolean(id)) || []}
            placeholder={requiresSizes ? 'Select material sizes' : 'No size required'}
            options={(requiresSizes ? sizes : []).map((size) => ({
              value: size.id,
              label: `${size.label} (${size.value})`,
            }))}
            onChange={(sizeIds) => {
              if (requiresSizes && !sizeIds.length) {
                message.error('Select at least one size for this material.');
                return;
              }

              const invalidSizeIds = sizeIds.filter((sizeId) => !allowedSizeIds.includes(sizeId));
              if (invalidSizeIds.length) {
                message.error('Selected sizes do not match this product type and gender.');
                return;
              }

              void onCommitMaterialSizes(material.id, requiresSizes ? sizeIds : []);
            }}
            className="admin-product-form__sizes-input"
          />
        </label>
        <AdminProductVariantStockList
          material={material}
          draftStockById={draftStockById}
          isSavingStock={isSavingStock}
          requiresSizes={requiresSizes}
          productId={productId}
          onStockStep={onStockStep}
          onDraftStockInput={onDraftStockInput}
          onCommitDraftStock={onCommitDraftStock}
          onVariantAvailabilityChange={onVariantAvailabilityChange}
        />
        <AdminProductMaterialImages
          material={material}
          productId={productId}
          onUploadMaterialImages={onUploadMaterialImages}
          onDeleteMaterialImage={onDeleteMaterialImage}
        />
      </div>
    </div>
  );
};

export default AdminProductStockControl;
